import React, { useEffect, useState } from 'react';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import StatStrip from '../../../components/shared/StatStrip/StatStrip';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Button from '../../../components/shared/Button/Button';
import Input from '../../../components/shared/Input/Input';
import { getParcelRevenue, downloadParcelRevenueCsv } from '../../../services/parcels';
import type { ParcelRevenueSummary, ParcelRevenueLineItem } from '../../../types/api';

function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const LINE_ITEM_COLUMNS: Column<ParcelRevenueLineItem>[] = [
  { key: 'tracking_code', header: 'Tracking' },
  { key: 'created_at', header: 'Date', render: (r) => new Date(r.created_at).toLocaleDateString() },
  { key: 'sender_name', header: 'Sender' },
  { key: 'shipping_fee', header: 'Total', render: (r) => `KES ${r.shipping_fee}` },
  { key: 'vat_amount', header: 'VAT', render: (r) => `KES ${r.vat_amount}` },
  { key: 'subtotal', header: 'Subtotal', render: (r) => `KES ${r.subtotal}` },
  { key: 'status', header: 'Status' },
];

const ParcelRevenue: React.FC = () => {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayIso());
  const [data, setData] = useState<ParcelRevenueSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getParcelRevenue({ start, end })
      .then((d) => { if (active) setData(d); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [start, end]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadParcelRevenueCsv({ start, end });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Parcel revenue"
        description="Parcel income and VAT for the selected period."
        actions={<Button onClick={handleDownload} loading={downloading}>Download CSV</Button>}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-medium text-slate-600">
          From
          <Input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            aria-label="Start date"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          To
          <Input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            aria-label="End date"
          />
        </label>
      </div>

      <StatStrip
        className="mb-6"
        stats={[
          { label: 'Parcels', value: data?.count ?? 0 },
          { label: 'Gross (incl VAT)', value: `KES ${data?.gross ?? '0.00'}` },
          { label: 'VAT 16%', value: `KES ${data?.vat ?? '0.00'}` },
          { label: 'Net (ex VAT)', value: `KES ${data?.net ?? '0.00'}` },
        ]}
      />

      <DataTable<ParcelRevenueLineItem>
        columns={LINE_ITEM_COLUMNS}
        data={data?.line_items ?? []}
        rowKey="tracking_code"
        loading={loading}
        emptyTitle="No parcel revenue"
        emptyDescription="No non-cancelled parcels in this period."
      />
    </>
  );
};

export default ParcelRevenue;
